import { arrow, autoUpdate, flip, FloatingArrow, FloatingPortal, hide, offset, ReferenceType, safePolygon, shift, useDismiss, useFloating, useHover, useInteractions } from '@floating-ui/react';
import { Check, EyeOff, Paperclip, RefreshCw } from 'lucide-react';
import { AnimatePresence, motion, useAnimationControls, useDragControls } from 'motion/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CollaborateTaskData } from 'types/api/collaborate.ts';
import { Avatar, Tooltip } from 'ui';
import { cn } from 'utils/cn.ts';
import {
  statusAvatarColor,
  textColor,
  urgencyAvatarColor,
} from 'utils/constants';

import { useFloatingUIManager } from '../../hooks/use-floating-manager.ts';
import { formatDateWithIntl } from '../../hooks/use-format-date.ts';
import { useAppDispatch, useAppSelector } from '../../store.ts';
import {
  addLostTask,
  clearPendingTask,
  getCurrentHoverStickerId,
  getLoadingAIComments,
  getPendingTask,
  getSelectedTaskId,
  getStickerAnimationState,
  PendingTask,
  removeLostTask,
  setCurrentHoverStickerId,
  setSelectedTask,
  setStickerAnimationState
} from '../../store/tasksSlice';
import { TaskWithAi } from '../../types/ai-task.ts';
import { getNearestNode } from '../../utils/node.ts';
import { StickerTypingIndicator } from '../tasks/sticker-typing-indicator.tsx';
import { TypingIndicator } from '../tasks/typing-indicator.tsx';

type RequiredTaskProperties = {
  task_comment_id: number;
  task_id: string;
  task_status: keyof typeof statusAvatarColor;
  task_priority: keyof typeof urgencyAvatarColor;
  is_internal: boolean;
  task_element_path?: string;
};

function isValidTask(task: Partial<CollaborateTaskData | PendingTask>, dummyTask?: boolean): task is RequiredTaskProperties {
  if (dummyTask) return true;

  // For pending tasks, just check if it has a task_id
  if (typeof task.task_id === 'string' && task.task_id.includes('PENDING')) {
    return true;
  }

  // For collaborate tasks, check all required properties
  if ('task_type' in task) {
    const collaborateTask = task as Partial<CollaborateTaskData>;
    return (
      typeof collaborateTask.task_comment_id === 'number' &&
      typeof collaborateTask.task_status === 'string' &&
      typeof collaborateTask.task_id === 'string' &&
      typeof collaborateTask.task_priority === 'string' &&
      typeof collaborateTask.is_internal === 'boolean' &&
      collaborateTask.task_status in statusAvatarColor &&
      collaborateTask.task_priority in urgencyAvatarColor
    );
  }

  return false;
}

interface Props {
  containerRef: React.RefObject<HTMLDivElement | null>;
  draggable?: boolean;
  positioned?: boolean;
  task: TaskWithAi;
  dummyTask?: boolean;
  isActiveSticker?: boolean;
  overridePosition?: { top: number, left: number } | null;
  isClusterExpanded?: boolean;
  inClusterPanel?: boolean;
  animationDelay?: number;
  shouldAnimate?: boolean;
  onAnimationComplete?: () => void;
  taskPanelSetReference?: (node: ReferenceType | null) => void;
}

export const Sticker = ({
  draggable,
  positioned,
  task,
  dummyTask,
  isActiveSticker,
  overridePosition,
  isClusterExpanded,
  inClusterPanel = false,
  animationDelay = 0,
  shouldAnimate = false,
  onAnimationComplete,
  taskPanelSetReference
}: Props) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const stickerAnimationState = useAppSelector(getStickerAnimationState);
  const [dragging, setDragging] = useState(false);
  const [hasBeenDragged, setHasBeenDragged] = useState(false);
  const loadingAiComments = useAppSelector(getLoadingAIComments);
  const pendingTask = useAppSelector(getPendingTask);
  const dragControls = useDragControls();
  const animationControls = useAnimationControls();
  const [validatedTask, setValidatedTask] = useState<RequiredTaskProperties | PendingTask | null>(null);
  const [validElementFound, setValidElementFound] = useState(false);
  const [hasFixedAncestor, setHasFixedAncestor] = useState(false);
  const targetElementRef = useRef<Element | null>(null);
  const lastPositionRef = useRef<{ top: number, left: number } | null>(null);
  const [dragEnabled, setDragEnabled] = useState(false);
  const hoverArrowRef = useRef<SVGSVGElement | null>(null);
  const selectedTaskId = useAppSelector(getSelectedTaskId);
  const currentHoverStickerId = useAppSelector(getCurrentHoverStickerId);

  // Animation control flags to prevent excessive animations
  const [hasInitiallyAnimated, setHasInitiallyAnimated] = useState(false);
  const [isCurrentlyAnimating, setIsCurrentlyAnimating] = useState(false);
  const lastClusterStateRef = useRef<boolean | undefined>(false);
  const animationTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  // Get portal root for FloatingPortal
  const getPortalRoot = useCallback(() => {
    const shadowRoot = document.getElementById('root-atarim')?.shadowRoot;
    if (shadowRoot) {
      const appWrapper = shadowRoot.getElementById('app-wrapper');
      return appWrapper || null; // Return null if app-wrapper doesn't exist yet
    }
    return null; // Return null to let FloatingPortal wait for the root and fallback to document.body
  }, []);

  // Use the floating UI manager hook
  const {
    shouldShowTaskPanel,
    openTaskPanel,
    closeTaskPanel,
    isTaskSelected
  } = useFloatingUIManager();

  // Check if this specific task should show its panel
  const showTaskPanel = validatedTask?.task_id ? shouldShowTaskPanel(validatedTask.task_id) : false;

  // Hover tooltip floating UI
  const [isHoverOpen, setIsHoverOpen] = useState(false);
  const hoverStickerId = validatedTask?.task_id ? `TASK:${validatedTask.task_id}` : '';

  const {
    refs: hoverRefs,
    floatingStyles: hoverFloatingStyles,
    context: hoverContext
  } = useFloating({
    open: isHoverOpen && selectedTaskId !== validatedTask?.task_id && currentHoverStickerId === hoverStickerId,
    onOpenChange: (open) => {
      if (hoverStickerId) {
        if (open) {
          dispatch(setCurrentHoverStickerId(hoverStickerId));
          setIsHoverOpen(true);
        } else {
          if (currentHoverStickerId === hoverStickerId) {
            dispatch(setCurrentHoverStickerId(null));
          }
          setIsHoverOpen(false);
        }
      } else {
        setIsHoverOpen(false);
      }
    },
    middleware: [
      offset(8),
      flip({
        padding: 16,
        fallbackPlacements:
          ['top', 'top-start', 'top-end', 'bottom-start', 'bottom-end'],
        fallbackStrategy: 'bestFit',
      }),
      shift({
        padding: 16,
      }),
      arrow({ element: hoverArrowRef, padding: 16 }),
      hide(),
    ],
    placement: 'bottom',
    whileElementsMounted: autoUpdate,
  });

  const hover = useHover(hoverContext, {
    delay: { open: 500, close: 0 },
    restMs: 100,
    handleClose: safePolygon({
      blockPointerEvents: true,
    }),
  });

  const dismiss = useDismiss(hoverContext, {
    referencePress: true,
    ancestorScroll: true,
    bubbles: {
      outsidePress: true,
      escapeKey: true,
    }
  });

  const { getReferenceProps: getHoverReferenceProps, getFloatingProps: getHoverFloatingProps } = useInteractions([
    hover,
    dismiss,
  ]);

  // Sync local hover open state with global id changes
  useEffect(() => {
    if (currentHoverStickerId !== hoverStickerId && isHoverOpen) {
      setIsHoverOpen(false);
    }
  }, [currentHoverStickerId, hoverStickerId, isHoverOpen]);


  // Ensure floating UI reference is always set
  const stickerRef = useRef<HTMLDivElement | null>(null);

  // Set refs when the sticker element is available
  const setRefs = useCallback((el: HTMLDivElement | null) => {
    stickerRef.current = el;

    // Always set hover ref if available
    if (el && typeof hoverRefs.setReference === 'function') {
      hoverRefs.setReference(el);
    }

    if (el && typeof taskPanelSetReference === 'function') {
      taskPanelSetReference(el);
    }
  }, [hoverRefs, taskPanelSetReference]);

  // Update task panel ref when selection changes
  useEffect(() => {
    if (stickerRef.current && typeof taskPanelSetReference === 'function') {
      const isSelectedTask = validatedTask?.task_id === selectedTaskId;

      // Track creation flow by task_comment_id
      const isSelectedPending = selectedTaskId?.includes('PENDING');
      const isSelectedOptimistic = selectedTaskId?.includes('OPTIMISTIC');
      const isSelectedReal = selectedTaskId && !selectedTaskId.includes('PENDING') && !selectedTaskId.includes('OPTIMISTIC');

      const selectedTaskCommentId = isSelectedPending || isSelectedOptimistic || isSelectedReal ?
        validatedTask?.task_comment_id : null;

      const shouldSetReference = isSelectedTask ||
        (validatedTask?.task_comment_id && selectedTaskCommentId && validatedTask.task_comment_id === selectedTaskCommentId);

      if (shouldSetReference) {
        taskPanelSetReference(stickerRef.current);
      } else {
        // Clear the reference if this task is no longer selected
        taskPanelSetReference(null);
      }
    }
  }, [validatedTask?.task_id, validatedTask?.task_comment_id, selectedTaskId, taskPanelSetReference]);

  // If this task becomes selected, ensure its hover is closed and global hover id cleared
  useEffect(() => {
    if (selectedTaskId && selectedTaskId === validatedTask?.task_id) {
      if (currentHoverStickerId === hoverStickerId) {
        dispatch(setCurrentHoverStickerId(null));
      }
      setIsHoverOpen(false);
    }
  }, [selectedTaskId, validatedTask?.task_id, currentHoverStickerId, hoverStickerId, dispatch]);

  // Detect cluster state changes to prevent excessive animations
  useEffect(() => {
    if (isClusterExpanded !== lastClusterStateRef.current) {
      lastClusterStateRef.current = isClusterExpanded;

      if (isClusterExpanded && hasInitiallyAnimated) {
        // Cluster just expanded - don't animate the stickers inside
        setIsCurrentlyAnimating(false);
      }
    }
  }, [isClusterExpanded, hasInitiallyAnimated]);

  // Clear animation timeout on unmount
  useEffect(() => {
    return () => {
      if (animationTimeoutRef.current) {
        clearTimeout(animationTimeoutRef.current);
      }
    };
  }, []);

  const stableKey = useMemo(() => {
    if (task.task_comment_id) {
      return `sticker-${task.task_comment_id}`;
    }
    return `sticker-${task.task_id}`;
  }, [task.task_comment_id, task.task_id]);

  const hasFixedPosition = (element: Element | null): boolean => {
    if (!element) return false;

    let currentElement: Element | null = element;

    while (currentElement && currentElement !== document.body && currentElement !== document.documentElement) {
      const style = window.getComputedStyle(currentElement);
      if (style.position === 'fixed') {
        return true;
      }
      currentElement = currentElement.parentElement;
    }

    return false;
  };

  const getInitialViewportPosition = useMemo(() => {
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const margin = 50;

    // Avoid the right third of the viewport for the sidebar
    const usableWidth = (viewportWidth * 2 / 3) - (2 * margin);
    const usableHeight = (viewportHeight / 2) - (2 * margin);

    const randomLeft = margin + Math.random() * usableWidth;
    const randomTop = margin + Math.random() * usableHeight;

    const scrollY = window.scrollY || document.documentElement.scrollTop;
    const scrollX = window.scrollX || document.documentElement.scrollLeft;

    return {
      top: randomTop + scrollY,
      left: randomLeft + scrollX
    };
  }, []);

  // Clamp horizontal position to stay within viewport width
  const clampToViewport = useCallback((pos: { top: number; left: number }) => {
    const H_MARGIN = 16;
    const viewportWidth = window.innerWidth;
    const scrollX = window.scrollX || document.documentElement.scrollLeft;
    const stickerWidth = stickerRef.current?.offsetWidth || 32;

    const minLeft = hasFixedAncestor ? H_MARGIN : scrollX;
    const maxLeft = (hasFixedAncestor ? 0 : scrollX) + viewportWidth - stickerWidth - H_MARGIN;

    const clampedLeft = Math.max(minLeft, Math.min(pos.left, maxLeft));
    return { top: pos.top, left: clampedLeft };
  }, [hasFixedAncestor]);

  const isElementValid = (targetElement: HTMLElement | null) => {
    if (!targetElement) return false;

    const rect = targetElement.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return false;

    const style = window.getComputedStyle(targetElement);
    if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') {
      return false;
    }

    const isAtOrigin = rect.top === 0 && rect.left === 0 && rect.right === 0 && rect.bottom === 0;
    if (isAtOrigin) return false;

    return true;
  }

  // Calculate position based on target element or use random position for dummy tasks
  const calculatePosition = useCallback(() => {
    if (inClusterPanel) {
      return { top: 0, left: 0 };
    }
    // If we have an override position (from clustering), use it
    if (overridePosition) {
      return overridePosition;
    }

    if (dummyTask) {
      return getInitialViewportPosition;
    }

    if (!targetElementRef.current) return { top: 0, left: 0 };

    const rect = targetElementRef.current.getBoundingClientRect();

    if (hasFixedAncestor) {
      // For elements with fixed ancestors, use viewport coordinates
      return {
        top: rect.top + (rect.height / 2),
        left: rect.left + (rect.width / 2)
      };
    } else {
      // For regular elements, use document coordinates
      const scrollY = window.scrollY || document.documentElement.scrollTop;
      const scrollX = window.scrollX || document.documentElement.scrollLeft;

      return {
        top: rect.top + scrollY + (rect.height / 2),
        left: rect.left + scrollX + (rect.width / 2)
      };
    }
  }, [dummyTask, hasFixedAncestor, overridePosition, inClusterPanel, getInitialViewportPosition]);

  // Throttled position update to prevent excessive re-renders
  const updateStickerPosition = useCallback(() => {
    // Don't update position if the user is dragging, has manually positioned the sticker,
    // or if we're currently animating
    if (dragging || hasBeenDragged || !validElementFound || isCurrentlyAnimating || inClusterPanel) return;

    const newPosition = clampToViewport(calculatePosition());

    // Only update if position has changed significantly (>2px) to avoid unnecessary re-renders
    if (
      !lastPositionRef.current ||
      Math.abs(newPosition.top - lastPositionRef.current.top) > 2 ||
      Math.abs(newPosition.left - lastPositionRef.current.left) > 2
    ) {
      // Use set instead of start to avoid triggering animations during position updates
      animationControls.set({
        position: hasFixedAncestor ? 'fixed' : 'absolute',
        top: isClusterExpanded ? overridePosition?.top || newPosition.top : newPosition.top,
        left: isClusterExpanded ? overridePosition?.left || newPosition.left : newPosition.left,
      });

      lastPositionRef.current = newPosition;
    }
  }, [animationControls, overridePosition, dragging, hasBeenDragged, validElementFound, inClusterPanel, hasFixedAncestor, calculatePosition, isClusterExpanded, isCurrentlyAnimating, clampToViewport]);

  useEffect(() => {
    if (isValidTask(task, dummyTask)) {
      setValidatedTask(task);
    } else {
      setValidatedTask(null);
    }
  }, [task, t, dummyTask]);

  useEffect(() => {
    const findElement = async () => {
      if (!dummyTask && validatedTask) {
        try {

          if (validatedTask?.task_element_path) {
            const targetElement = await getNearestNode(
              validatedTask.task_element_path
            );
            if (!targetElement) {
              console.warn(`No element found for task ${validatedTask.task_id} at path ${validatedTask.task_element_path}`);
            }

            targetElementRef.current = targetElement;

            const validElementFound = isElementValid(targetElement);
            const elementHasFixedAncestor = hasFixedPosition(targetElement);

            setValidElementFound(validElementFound);
            setHasFixedAncestor(elementHasFixedAncestor);

          }
          // Report to Redux if the element was not found
          if (!targetElementRef.current && !validElementFound && validatedTask.task_id) {
            dispatch(addLostTask(validatedTask.task_id));
          } else if (validElementFound && validatedTask.task_id) {
            dispatch(removeLostTask(validatedTask.task_id));
          }
        } catch (error) {
          console.error('Error finding element:', error);
        }
      } else if (dummyTask) {
        setValidElementFound(true);
        setHasFixedAncestor(false);
      }
    };

    findElement();
  }, [
    validatedTask,
    validElementFound,
    dispatch,
    dummyTask,
  ]);

  // Add event listeners for scroll and resize - with throttling
  useEffect(() => {
    if (!validElementFound || dummyTask || hasBeenDragged || isClusterExpanded) return;

    let timeoutId: NodeJS.Timeout;
    const handleScrollOrResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        if (!dragging && !isCurrentlyAnimating) {
          requestAnimationFrame(updateStickerPosition);
        }
      }, 16); // ~60fps throttling
    };

    window.addEventListener('scroll', handleScrollOrResize, { passive: true });
    window.addEventListener('resize', handleScrollOrResize, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScrollOrResize);
      window.removeEventListener('resize', handleScrollOrResize);
      clearTimeout(timeoutId);
    };
  }, [validElementFound, hasBeenDragged, dummyTask, dragging, updateStickerPosition, isClusterExpanded, isCurrentlyAnimating]);

  useEffect(() => {
    if (dragging) {
      const originalUserSelect = document.body.style.userSelect;
      document.body.style.userSelect = 'none';
      return () => {
        document.body.style.userSelect = originalUserSelect;
      };
    }
  }, [dragging]);

  // Optimized initial positioning with animation control
  useEffect(() => {
    if ((dummyTask || (validatedTask && validElementFound && validatedTask.task_comment_id)) && !hasInitiallyAnimated) {
      if (inClusterPanel) {
        // In cluster panel - handle staggered animation
        if (shouldAnimate) {
          animationControls.set({
            scale: 0,
            position: 'relative',
            opacity: 0,
          });

          const timer = setTimeout(() => {
            animationControls.start({
              scale: 1,
              opacity: 1,
              transition: {
                type: "spring",
                stiffness: 400,
                damping: 20,
                duration: 0.25
              }
            }).then(() => {
              setIsCurrentlyAnimating(false);
              setHasInitiallyAnimated(true);
              onAnimationComplete?.();
            });
          }, animationDelay);

          return () => clearTimeout(timer);
        } else {
          // No animation needed
          animationControls.set({
            scale: 1,
            position: 'relative',
            opacity: 1,
          });
          setHasInitiallyAnimated(true);
          setIsCurrentlyAnimating(false);
          onAnimationComplete?.();
        }
        return;
      }
      // Disable drag during positioning to avoid conflicts
      setDragEnabled(false);
      setIsCurrentlyAnimating(true);

      const initialPos = clampToViewport(calculatePosition());
      lastPositionRef.current = initialPos;

      if (shouldAnimate) {
        // Initial scaling animation for new stickers only
        animationControls.set({
          position: hasFixedAncestor ? 'fixed' : 'absolute',
          top: initialPos.top,
          left: initialPos.left,
          scale: 0,
        });

        animationControls.start({
          scale: 1,
          transition: {
            type: "spring",
            stiffness: 400,
            damping: 20,
            duration: 0.25
          }
        }).then(() => {
          setIsCurrentlyAnimating(false);
          setHasInitiallyAnimated(true);
          if (validatedTask) {
            dispatch(setStickerAnimationState({ comment_id: validatedTask.task_comment_id, state: true }));
          }
        });
      } else {
        // Just position without animation for cluster expansions
        animationControls.set({
          position: hasFixedAncestor ? 'fixed' : 'absolute',
          top: initialPos.top,
          left: initialPos.left,
          scale: 1,
        });
        setIsCurrentlyAnimating(false);
        if (!dummyTask) {
          setHasInitiallyAnimated(true);
        }
      }

      // Re-enable drag after positioning with a delay
      if (positioned && draggable) {
        animationTimeoutRef.current = setTimeout(() => {
          setDragEnabled(true);
        }, shouldAnimate ? 300 : 100);
      }
    }
  }, [
    validatedTask,
    validElementFound,
    hasFixedAncestor,
    dummyTask,
    positioned,
    draggable,
    animationControls,
    calculatePosition,
    clampToViewport,
    stickerAnimationState,
    dispatch,
    hasInitiallyAnimated,
    isClusterExpanded,
    inClusterPanel,
    shouldAnimate,
    animationDelay,
    onAnimationComplete
  ]);

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!validatedTask?.task_id || dragging || isCurrentlyAnimating) return;

    if (hasBeenDragged) {
      resetPosition(e as unknown as React.MouseEvent<SVGSVGElement>);
      return;
    }

    // Prevent default to avoid focus issues
    e.preventDefault();
    e.stopPropagation();

    const isCurrentlySelected = isTaskSelected(validatedTask.task_id);

    if (isCurrentlySelected) {
      closeTaskPanel();
      dispatch(setSelectedTask(undefined));
    } else {
      openDialog();
    }
  };

  const handleDragStart = () => {
    if (!draggable || !dragEnabled || isCurrentlyAnimating) return;
    closeTaskPanel();
    setDragging(true);
  };

  const resetPosition = (e: React.MouseEvent<SVGSVGElement>) => {
    e.stopPropagation();
    closeTaskPanel();
    setHasBeenDragged(false);
    setIsCurrentlyAnimating(true);

    const newPosition = clampToViewport(calculatePosition());
    lastPositionRef.current = newPosition;

    if (inClusterPanel) {
      animationControls.set({
        position: 'relative',
        top: newPosition.top,
        left: newPosition.left,
        x: 0,
        y: 0,
      });
    } else {
      animationControls.start({
        position: hasFixedAncestor ? 'fixed' : 'absolute',
        top: newPosition.top,
        left: newPosition.left,
        x: 0,
        y: 0,
        transition: { type: "spring", stiffness: 200, damping: 20 }
      }).then(() => {
        setIsCurrentlyAnimating(false);
      });
    }
  };

  const openDialog = async () => {
    if (!validatedTask?.task_id || isCurrentlyAnimating) return;

    if (validatedTask.task_id !== pendingTask?.task_id) {
      dispatch(clearPendingTask());
    }

    if (!hasBeenDragged && !pendingTask && !dummyTask) {
      openTaskPanel(validatedTask.task_id);
    } else {
      openTaskPanel(validatedTask.task_id);
    }
  };

  const handleDragEnd = () => {
    if (!draggable) return;
    setDragging(false);
    setHasBeenDragged(true);
  };



  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!validatedTask?.task_id || dragging || isCurrentlyAnimating) return;
    e.stopPropagation();

    const isCurrentlySelected = isTaskSelected(validatedTask.task_id);

    if (isCurrentlySelected) {
      closeTaskPanel();
      dispatch(setSelectedTask(undefined));
    } else {
      openDialog();
    }
  };

  const hasFileAttached = (task: CollaborateTaskData) =>
    task.task_comment_message === '<p><br></p>' ||
    task.task_comment_message === '' ||
    task.task_comment_message === null ||
    task.task_comment_message === undefined ||
    task.task_comment_message.includes('<img') ||
    task.task_comment_message.includes('<video') ||
    task.task_comment_message.includes('<audio') ||
    task.task_comment_message.includes('<iframe') ||
    task.task_comment_message.includes('<embed') ||
    task.task_comment_message.includes('<object') ||
    task.task_comment_message.includes('<applet') ||
    task.task_comment_message.includes('<param');

  const extractTextFromHTML = (html: string, task: CollaborateTaskData) => {
    if (hasFileAttached(task)) return t('task.file_attachment', 'File attachment');
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.body.textContent || '';
  };

  const StickyContent = () => {
    if (!validatedTask) return null;

    if (validatedTask.task_status === 'complete') {
      return (
        <div className="bg-primary relative flex h-8 w-8 items-center justify-center overflow-hidden rounded-[15%_50%_50%_50%] p-1 shadow-md group">
          <div className="bg-primary absolute right-0 top-0 h-full w-full -translate-y-1/2 translate-x-1/2 transform rounded-full" />
          <span className="relative z-10 text-xs font-normal text-white">
            <Check
              size={16}
              strokeWidth={3}
              aria-label={t('task_sticker.aria.complete', 'Task complete')}
              className="transition-opacity duration-200 ease-in-out group-hover:opacity-0"
            />
            {validatedTask.task_comment_id > 0 &&
              <span className="absolute inset-0 flex items-center justify-center transition-opacity duration-200 ease-in-out opacity-0 group-hover:opacity-100">
                {validatedTask.task_comment_id}
              </span>
            }
          </span>
        </div>
      );
    }

    const urgencyColor = urgencyAvatarColor[validatedTask.task_priority];
    const statusColor = statusAvatarColor[validatedTask.task_status];
    const fontColor = textColor[validatedTask.task_status as keyof typeof textColor];

    return (
      <div
        key={`sticker-${validatedTask.task_comment_id}`}
        className="relative flex h-8 w-8 items-center justify-center overflow-hidden rounded-[15%_50%_50%_50%] p-1 shadow-md outline outline-2 outline-white"
        style={{ backgroundColor: statusColor, color: fontColor }}
        aria-label={t('task_sticker.aria.task_number', 'Task number {{number}}', { number: validatedTask.task_comment_id })}
      >
        <div
          className="absolute right-0 top-0 h-full w-full -translate-y-1/2 translate-x-1/2 transform rounded-full"
          style={{ backgroundColor: urgencyColor, color: fontColor }}
        />
        <span className="relative z-10 text-xs font-normal">
          {validatedTask.is_internal ? (
            <EyeOff
              size={18}
              style={{ color: fontColor }}
              aria-label={t('task_sticker.aria.internal', 'Internal task')}
            />
          ) : hasBeenDragged ? (
            <Tooltip content={t('task_sticker.tooltip.reset_position', 'Reset position')} className="cursor-pointer">
              <RefreshCw
                size={18}
                onClick={resetPosition}
                data-no-drag
                aria-label={t('task_sticker.aria.reset', 'Reset task position')}
              />
            </Tooltip>
          ) : (
            validatedTask.task_comment_id
          )}
        </span>
      </div>
    );
  };

  if (!validatedTask) return null;

  if (positioned && !validElementFound && validatedTask?.task_element_path && !dummyTask) {
    return null;
  }

  const renderSticker = () => (
    <AnimatePresence>
      <motion.div
        key={stableKey}
        initial={{
          scale: inClusterPanel ? 1 : 0, // Don't animate scale in cluster panels
        }}
        drag={dragEnabled && !inClusterPanel} // Disable drag in cluster panels
        dragControls={dragControls}
        animate={animationControls}
        onAnimationComplete={() => {
          if (validatedTask) {
            dispatch(setStickerAnimationState({ comment_id: validatedTask.task_comment_id, state: true }));
          }
        }}
        dragMomentum={false}
        dragElastic={0.2}
        dragPropagation={false}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        style={inClusterPanel ? {
          // In cluster panel - use relative positioning
          position: 'relative',
          zIndex: showTaskPanel ? 50 : 10,
          cursor: 'pointer',
        } : {
          // Normal positioning
          position: hasFixedAncestor ? 'fixed' : 'absolute',
          zIndex: showTaskPanel ? 50 : 10,
          cursor: dragging ? 'grabbing' : 'grab',
        }}
        className={"group/sticker"}
        onPointerDown={handlePointerDown}
        ref={setRefs}
      >
        {isActiveSticker && <StickerTypingIndicator />}
        <div
          className={cn(
            'pointer-events-auto cursor-pointer group-hover/sticker:opacity-80',
            dragging && 'opacity-50 cursor-grabbing',
            inClusterPanel && 'hover:scale-105 transition-transform'
          )}
          onClick={handleClick}
          id={`task-sticker-${validatedTask?.task_comment_id}`}
          {...getHoverReferenceProps()} // Always apply hover props
        >
          <StickyContent />
        </div>
      </motion.div>
    </AnimatePresence>
  );

  // Render hover tooltip - always in FloatingPortal when in cluster panel
  const renderHoverTooltip = () => {
    if (!isHoverOpen || selectedTaskId === validatedTask?.task_id) return null;

    const tooltip = (
      <div
        ref={hoverRefs.setFloating}
        style={{
          ...hoverFloatingStyles,
          zIndex: 40,
        }}
        {...getHoverFloatingProps()}
        onClick={handleClick}
        className="relative pointer-events-auto cursor-pointer bg-background min-w-0 w-80 max-w-96 p-2.5 rounded-2xl border border-border shadow-md"
      >
        <FloatingArrow
          ref={hoverArrowRef}
          context={hoverContext}
          stroke="#e2e2e2"
          strokeWidth={1}
          className="fill-background"
        />
        {'latest_comment' in task && (
          <div className="flex gap-2">
            <Avatar
              src={task.task_created_by_preview?.avatar || task.latest_comment?.author?.avatar}
              title={task.task_created_by_preview?.name || task.latest_comment?.author?.user_name}
              alt={task.task_created_by_preview?.name || task.latest_comment?.author?.user_name}
              id={task.task_created_by_preview?.normal_id}
              size="md"
            />
            <div className="flex flex-col grow">
              <div className="leading-0 inline-flex items-center text-sm gap-2">
                <span className="truncate font-medium max-w-[130px] text-textcolor">{task.task_created_by_preview?.name || task.task_created_by}</span>
                {hasFileAttached(task) && (
                  <Tooltip content={t('task.tooltip.has_attachments', 'This comment has items attached')}>
                    <Paperclip size={14} strokeWidth={2} />
                  </Tooltip>
                )}
                <Tooltip content={formatDateWithIntl(task.task_created_time ?? '')}>
                  <span className="font-normal text-xs text-gray-400 tracking-normal">
                    {t('task.created_ago', '{{time}} ago', { time: task.task_created_time_ago })}
                  </span>
                </Tooltip>
              </div>
              {task.task_id && loadingAiComments[task.task_id] === 'running' ? (
                <TypingIndicator taskId={task.task_id}
                  onlyAI={true}
                  className="text-wrap text-sm font-normal max-w-[200px] truncate items-start mt-0 leading-0"
                  indicatorClassName="hidden"
                />
              ) : (
                <p className="line-clamp-2 text-wrap text-sm font-normal text-textcolor">
                  {extractTextFromHTML(task.task_title!, task)}
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    );

    // Always use FloatingPortal for hover tooltips to ensure proper positioning
    return (
      <FloatingPortal root={getPortalRoot()}>
        {tooltip}
      </FloatingPortal>
    );
  };

  if (!validatedTask) return null;

  if (positioned && !validElementFound && validatedTask?.task_element_path && !dummyTask) {
    return null;
  }

  return (
    <>
      {renderSticker()}
      {renderHoverTooltip()}
    </>
  );
};